import { Observable, throwError } from 'rxjs'
import { finalize, map, tap } from 'rxjs/operators'
import { ReviewModel, ReviewPage } from '../ngrx-store/app.state'

/**
 * A PPA's reviews, a page at a time - for the PPA page (/ppa/:id) and the card's reviews dialog.
 * It starts from GET /ppas/:id's first page (`Reviews`, `reviews_next_cursor`) and "Show more"
 * fetches the next (GET /ppas/:id/reviews?before=). The cursor is a review id, so a review posted
 * meanwhile doesn't shift the pages; one already shown is skipped anyway.
 */
export class PpaReviewPager {
  reviews: ReviewModel[] = []
  nextCursor: number | null = null
  loadingMore = false
  loadMoreFailed = false

  constructor(private fetchPage: (before: number) => Observable<ReviewPage>) {}

  /** The first page, as GET /ppas/:id sends it. No cursor: that's all of them. */
  start(reviews: ReviewModel[] | undefined, nextCursor: number | null | undefined): void {
    this.reviews = reviews ?? []
    this.nextCursor = nextCursor ?? null
    this.loadingMore = false
    this.loadMoreFailed = false
  }

  get hasMore(): boolean {
    return this.nextCursor !== null
  }

  /** The next page, added to the end. Emits the reviews it added (for focus). */
  loadMore(): Observable<ReviewModel[]> {
    if (this.nextCursor === null || this.loadingMore) {
      return throwError(() => new Error('Nothing more to load'))
    }
    this.loadingMore = true
    this.loadMoreFailed = false
    return this.fetchPage(this.nextCursor).pipe(
      map((page) => {
        const shown = new Set(this.reviews.map((review) => review.id))
        const added = (page.reviews ?? []).filter((review) => !shown.has(review.id))
        this.reviews = [...this.reviews, ...added]
        this.nextCursor = page.next_cursor ?? null
        return added
      }),
      tap({ error: () => (this.loadMoreFailed = true) }),
      finalize(() => (this.loadingMore = false)),
    )
  }

  /** A review just posted goes on top. */
  prepend(review: ReviewModel): void {
    this.reviews = [review, ...this.reviews.filter((shown) => shown.id !== review.id)]
  }
}
